import React from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SalesOpsHeader from './SalesOpsHeader';
import {
  AssigneeChip,
  Card,
  MethodNote,
  ReasoningBox,
  Shimmer,
  TableSkeleton,
  money,
  pct,
  tableSx,
} from './salesOpsKit';
import { fetchSalesOpsQueue } from '../../services/salesOperationsService';
import type { SalesOpsQueue, SalesOpsQueueLead } from '../../types/salesOperations';

/**
 * Queue -- the hero screen.
 *
 * Handover 2.5: "This is where the entire argument becomes visible. Build
 * this one properly; everything else is context."
 *
 * The argument is that the recommendation is a *decision*, not a sort order,
 * so the decision panel has to show what was rejected and why. Every row's
 * `options` carry the agents that were considered -- including ones with
 * better close rates than the winner, which is the point.
 */
export default function SalesOpsQueuePage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState<SalesOpsQueue | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<string | null>(null);

  const load = React.useCallback((force = false) => {
    const controller = new AbortController();
    setLoading(true);
    fetchSalesOpsQueue(25, controller.signal, force)
      .then((payload) => {
        setData(payload);
        setSelected(payload.leads[0]?.lead_id ?? null);
        setError(null);
      })
      .catch((e: Error) => {
        if (e.name !== 'AbortError') setError(e.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  React.useEffect(() => load(), [load]);

  const lead = data?.leads.find((l) => l.lead_id === selected) ?? null;
  const pending = loading || !data;

  return (
    <Box>
      <SalesOpsHeader
        title="Queue"
        description="Callable inventory ranked by expected margin, and who should take each lead"
        asOf={data?.anchor_date}
        computedAt={data?.computed_at}
        fromCache={data?.from_cache}
        onRefresh={() => load(true)}
        refreshing={loading}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!error && (
        <Stack spacing={2}>
          <Card title="Ranked by expected margin" dense>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Lead</th>
                  <th>State</th>
                  <th>Age</th>
                  <th>Attempt</th>
                  <th className="num">Expected value</th>
                  <th className="num">Reach</th>
                  <th>Best hour</th>
                  <th>Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {!pending && data?.leads.map((l) => (
                  <QueueRow
                    key={l.lead_id}
                    lead={l}
                    selected={l.lead_id === selected}
                    onSelect={() => setSelected(l.lead_id)}
                  />
                ))}
              </tbody>
            </Box>
            {pending && <TableSkeleton rows={8} cols={7} />}
            <Box sx={{ px: 2, pb: 2 }}>
              {pending ? <Shimmer width="85%" height={12} sx={{ mt: 1 }} /> : (
              <MethodNote>
                {data!.formula} Expected value is what the policy is worth if the lead is
                worked to a decision; Reach is the chance of getting there. The list
                sorts on the two multiplied, so a valuable lead nobody can still
                reach does not outrank a workable one.
              </MethodNote>
              )}
            </Box>
          </Card>

          {pending && <DecisionPanelSkeleton />}
          {!pending && lead && <DecisionPanel lead={lead} onAskCopilot={() => navigate('/copilot')} />}
        </Stack>
      )}
    </Box>
  );
}

/** Mirrors the decision panel's shape so the page does not reflow when it lands. */
function DecisionPanelSkeleton() {
  return (
    <Card>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Shimmer width={160} height={20} />
          <Shimmer width={220} height={13} />
        </Stack>
        <Box>
          <Shimmer width={130} height={11} sx={{ mb: 1.25 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.25,
            }}
          >
            {[0, 1, 2, 3].map((i) => <Shimmer key={i} height={62} />)}
          </Box>
        </Box>
        <Box>
          <Shimmer width={130} height={11} sx={{ mb: 1.25 }} />
          <TableSkeleton rows={4} cols={6} />
        </Box>
        <Shimmer height={54} />
        <Stack direction="row" spacing={1.25}>
          <Shimmer width={92} height={32} />
          <Shimmer width={210} height={32} />
          <Shimmer width={130} height={32} />
        </Stack>
      </Stack>
    </Card>
  );
}

function QueueRow({
  lead,
  selected,
  onSelect,
}: {
  lead: SalesOpsQueueLead;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Box
      component="tr"
      onClick={onSelect}
      sx={(t) => ({
        cursor: 'pointer',
        borderTop: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
        bgcolor: selected ? alpha(t.palette.primary.main, 0.08) : 'transparent',
        boxShadow: selected ? `inset 3px 0 0 ${t.palette.primary.main}` : 'none',
        '&:hover': {
          bgcolor: selected
            ? alpha(t.palette.primary.main, 0.1)
            : t.tokens?.surface?.sunken ?? 'action.hover',
        },
      })}
    >
      <td style={{ color: 'var(--mui-palette-text-disabled)' }}>{lead.rank}</td>
      <td><strong>{lead.name}</strong></td>
      <td>{lead.state_cd}</td>
      <td>{lead.age_days === 0 ? 'Day 0' : `Day ${lead.age_days}`}</td>
      <td>{lead.attempt_cnt}</td>
      <td className="num"><strong>{money(lead.expected_value)}</strong></td>
      <td className="num">{pct(lead.probability_reached, 0)}</td>
      <td>{lead.best_hour_label ?? '–'}</td>
      <td><AssigneeChip name={lead.assigned_to} type={lead.assigned_type} /></td>
    </Box>
  );
}

/**
 * The decision panel (handover 2.5 a-d): why it ranks here, who should take
 * it, the plain-English reason, and the actions.
 */
function DecisionPanel({
  lead,
  onAskCopilot,
}: {
  lead: SalesOpsQueueLead;
  onAskCopilot: () => void;
}) {
  const rec = lead.recommended;
  return (
    <Card>
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="baseline" spacing={1.25}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{lead.full_name}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            {lead.state_cd} · Day {lead.age_days} · attempt {lead.attempt_cnt}
            {lead.segment ? ` · ${lead.segment}` : ''}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <AssigneeChip name={lead.assigned_to} type={lead.assigned_type} />
        </Stack>

        {/* (a) Why it's ranked here -- the four metric cards. */}
        <Box>
          <SectionLabel>Why it&apos;s ranked here</SectionLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.25,
            }}
          >
            <Fact
              label={`Answers at ${lead.best_hour_label ?? 'best hour'}`}
              value={pct(lead.connect_at_best_hour, 1)}
            />
            <Fact label="Likely premium" value={money(lead.expected_premium)} />
            <Fact label="Carrier commission" value={pct(rec.commission_rate, 0)} />
            <Fact label="Persists 12 months" value={pct(rec.persistency, 0)} />
          </Box>
        </Box>

        {/* (b) Who should take it. The table has to make close rate and margin
            visibly diverge -- that is the finding, and it only lands if the
            rejected options are on screen next to the winner. */}
        <Box>
          <SectionLabel>Who should take it</SectionLabel>
          <Box
            component="table"
            sx={(t) => ({
              ...tableSx,
              border: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
              borderRadius: 1,
            })}
          >
            <thead>
              <tr>
                <th>Option</th>
                <th className="num">Closes</th>
                <th className="num">Persistency</th>
                <th className="num">Agent cost</th>
                <th className="num">Margin per lead</th>
                <th>Availability</th>
              </tr>
            </thead>
            <tbody>
              {lead.options.map((o) => {
                const isRec = o.agent_id === rec.agent_id;
                return (
                  <Box
                    component="tr"
                    key={o.agent_id}
                    sx={(t) => ({
                      bgcolor: isRec ? alpha(t.palette.success.main, 0.1) : 'transparent',
                      borderTop: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
                    })}
                  >
                    <td><strong>{o.name}</strong></td>
                    <td className="num">{pct(o.close_rate, 0)}</td>
                    <td className="num">{pct(o.persistency, 0)}</td>
                    <td className="num">{money(o.agent_cost)}</td>
                    <td className="num"><strong>{money(o.margin)}</strong></td>
                    <td>
                      <Typography
                        sx={{ fontSize: 12, color: o.on_shift ? 'success.main' : 'text.disabled' }}
                      >
                        {o.on_shift ? 'On shift' : 'Off shift'}
                      </Typography>
                    </td>
                  </Box>
                );
              })}
            </tbody>
          </Box>
          <MethodNote>
            Only agents licensed in {lead.state_cd} appear here — an unlicensed dial is a
            compliance incident, not an option. Availability breaks ties between
            candidates; it never overrides margin.
          </MethodNote>
        </Box>

        {/* (c) Plain English, assembled from the same factors shown above. */}
        <ReasoningBox>{lead.reasoning}</ReasoningBox>

        {/* (d) Actions. Deliberately no "send to dialer" -- see the note in
            the page docs; write-back to the dialer is not live and the demo
            must not imply it is (handover 3.5). */}
        <Stack direction="row" spacing={1.25}>
          <Button variant="outlined" size="small">Override</Button>
          <Button variant="outlined" size="small" onClick={onAskCopilot}>
            Ask CoPilot about this decision
          </Button>
          <Tooltip
            arrow
            title="Klaris decides which conversations are worth having. The dialer still places the call — write-back into it is not enabled."
          >
            <span>
              <Button variant="contained" size="small" disabled>
                Queue for dialer
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.disabled',
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={(t) => ({
        bgcolor: t.tokens?.surface?.sunken ?? 'action.hover',
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
      })}
    >
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 19, fontWeight: 600, mt: 0.25 }}>{value}</Typography>
    </Box>
  );
}
